import React, { useEffect, useState } from "react";
import { Activity, Loader2, Trash2, X } from "lucide-react";
import { idbDelete, idbList } from "../../utils/telemetry/idb";
import { summariseTelemetry } from "../../utils/telemetry/summary";
import {
  entryWindowMs,
  findOverlappingSession,
  loadDismissed,
  recordDismissed,
  savedTraceSeconds,
  selectRecoverableEntry,
} from "../../utils/telemetry/recovery";
import { getWorkoutSettings } from "../../hooks/useWorkoutSettings";
import * as api from "../../services/api";
import { Button } from "../ui/button";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const fmtDuration = (s) => {
  if (s == null) return "0 s";
  if (s < 60) return `~${s} s`;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return sec > 0 ? `~${m} min ${sec} s` : `~${m} min`;
};

const sessionName = (s) => s?.plan_data?.label || s?.workout_type || "your session";

// Saved sessions that could hold this recording: from a little before it
// started (a session starts with its count-in) to when it stopped.
const findSavedSession = async (entry) => {
  const win = entryWindowMs(entry);
  if (!win) return null;
  try {
    const sessions = await api.getSessions({
      from: new Date(win[0] - 6 * 3_600_000).toISOString(),
      to: new Date(win[1] + 60_000).toISOString(),
      limit: 20,
    });
    return findOverlappingSession(entry, sessions);
  } catch {
    return null; // Offline or signed out: offer the recording as before.
  }
};

const InFlightRecoveryBanner = () => {
  const [entry, setEntry] = useState(null);
  // The saved session this recording overlaps, if any.
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await idbList();
      const idleThresholdMs =
        (getWorkoutSettings().recoveryIdleThresholdMinutes ?? 10) * 60_000;
      const { entry: chosen, pruneSessionIds } = selectRecoverableEntry(
        entries,
        Date.now(),
        loadDismissed(),
        { idleThresholdMs },
      );
      // Silently drop entries that passed the time gate but hold no real data
      // so they stop nagging on future loads.
      for (const sessionId of pruneSessionIds) await idbDelete(sessionId);
      const match = chosen ? await findSavedSession(chosen) : null;
      if (!cancelled) {
        setSaved(match);
        setEntry(chosen);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!entry) return null;

  const sampleCount = entry.samples?.length || entry.sampleCount || 0;
  const lastT = entry.samples?.[entry.samples.length - 1]?.t ?? 0;
  const durationLabel = fmtDuration(lastT);
  const timeLabel = fmtTime(entry.startedAt);
  const canAttach = !!saved?.id && savedTraceSeconds(saved) === 0;

  const handleSave = async () => {
    setBusy(true);
    try {
      const samples = entry.samples || [];
      const summary = summariseTelemetry(samples);
      await api.createSession({
        started_at: entry.startedAt,
        ended_at: new Date().toISOString(),
        duration_minutes: Math.max(1, Math.round(summary.duration_s / 60)),
        workout_type: "recovered",
        plan_data: null,
        notes: "Recovered from interrupted workout",
        rating: null,
        telemetry: samples,
        telemetry_summary: summary,
      });
      await idbDelete(entry.sessionId);
      setEntry(null);
    } catch (err) {
      console.error("[Khronos] recovery save failed", err);
      // Leave the entry in place so the user can retry.
    } finally {
      setBusy(false);
    }
  };

  // The session was saved without this trace (a live session the server
  // filed, or the Coach lost it): put it on that session, not in a new one.
  const handleAttach = async () => {
    setBusy(true);
    try {
      const samples = entry.samples || [];
      await api.updateSession(saved.id, {
        telemetry: samples,
        telemetry_summary: { ...(saved.telemetry_summary || {}), ...summariseTelemetry(samples) },
      });
      await idbDelete(entry.sessionId);
      setEntry(null);
    } catch (err) {
      console.error("[Khronos] recovery attach failed", err);
    } finally {
      setBusy(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm("Discard this recovered workout? This can't be undone."))
      return;
    setBusy(true);
    try {
      await idbDelete(entry.sessionId);
      setEntry(null);
    } finally {
      setBusy(false);
    }
  };

  // Non-destructive: hide the banner and remember the dismissal so it stops
  // re-nagging, but keep the data in IDB so it can still be saved later.
  const handleClose = () => {
    recordDismissed(entry.sessionId);
    setEntry(null);
  };

  return (
    <div className="sticky top-0 z-40 w-full bg-yellow-500/10 border-b border-yellow-500/30 backdrop-blur-sm">
      <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-3 px-4 py-2">
        <Activity className="w-4 h-4 text-yellow-400 shrink-0" />
        <p className="text-sm text-yellow-100 flex-1 min-w-[12rem]">
          {!saved ? (
            <>
              We found an interrupted workout
              {timeLabel ? ` from ${timeLabel}` : ""},{" "}
              <span className="font-medium">{durationLabel}</span> recorded
              {sampleCount > 0 ? ` (${sampleCount} samples)` : ""}.
            </>
          ) : canAttach ? (
            <>
              <span className="font-medium">{sessionName(saved)}</span>
              {timeLabel ? ` from ${timeLabel}` : ""} was saved without its
              heart-rate trace. We kept it here:{" "}
              <span className="font-medium">{durationLabel}</span>.
            </>
          ) : (
            <>
              We found <span className="font-medium">{durationLabel}</span>{" "}
              recorded{timeLabel ? ` from ${timeLabel}` : ""}, during{" "}
              <span className="font-medium">{sessionName(saved)}</span>, which is
              already saved with {fmtDuration(savedTraceSeconds(saved)).replace("~", "")}{" "}
              of trace.
            </>
          )}
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {canAttach ? (
            <Button size="sm" onClick={handleAttach} disabled={busy}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Add to session
            </Button>
          ) : (
            <Button
              size="sm"
              variant={saved ? "secondary" : "default"}
              onClick={handleSave}
              disabled={busy}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {saved ? "Save separately" : "Save"}
            </Button>
          )}
          <Button
            size="sm"
            variant="destructive"
            onClick={handleDiscard}
            disabled={busy}
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Discard
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9"
            onClick={handleClose}
            disabled={busy}
            aria-label="Close — keep it to save later"
            title="Close — keep it to save later"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InFlightRecoveryBanner;
